import b from 'b_';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ym from 'react-yandex-metrika';

import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { Input, TableCell, Text } from 'platform-components';
import { KEY_CODES } from 'platform-components/constants';
import { getIsParent, getMe } from 'platform-components/src/components/user/selectors';
import getStudentName, { STUDENT_NAME_MAX_LENGTH } from 'platform-components/src/utils/get-student-name';
import { TooltipStateful } from 'platform-components/src/components/LegoTooltip/LegoTooltip';

import AcceptedIcon from 'common.components/students/__accepted-icon/students__accepted-icon';
import { StudentNamePencilIcon } from 'common.components/students/__student-name-pencil-icon/students__student-name-pencil-icon';
import {
    getCurrentEditingStudentIndex,
    getCurrentEditingStudentName,
    getEditStudentNameStatus,
    getIsAddingNewStudent,
    getStudentValidationById,
    getNewStudentEditingStudentName,
} from 'common.components/students/selectors';
import { NEW_STUDENT_ID } from 'common.components/students/students.constants';
import { Student } from 'common.components/typings';
import { getCanEditClass } from 'common.components/user/selectors';
import { ReduxUserStore } from 'common.components/user/typings';
import { YM_LOCATION } from 'utils/ym-constants';

import * as studentsActions from '../actions';
import { setStudentValidation } from '../actions';

import './students__student-name-table-cell.scss';

interface Props {
    isArchiveClass: boolean;
    student: Student;
    classId: number;
    errorMsgId: string | null;
    focused: boolean;
    onNameChange: () => void;
    onEnterKeyDown: () => void;
    onInputBlur: () => void;
    onInputChange: () => void;
    onInputFocus: () => void;
    shouldShowError: boolean;
    studentsCount: number;
}

// eslint-disable-next-line complexity
export const StudentNameTableCell = (props: Props) => {
    const dispatch = useDispatch();

    const {
        isArchiveClass,
        student,
        studentsCount,
        focused,
        classId,
        errorMsgId,
        shouldShowError,
        onNameChange,
        onEnterKeyDown,
        onInputBlur,
        onInputChange,
        onInputFocus,
    } = props;

    const { setCurrentEditingStudentName, editStudentNameRequest, saveNewStudentRequest } = studentsActions;

    const {
        firstName,
        lastName,
        loginId,
        id,
        is_personal_data_accepted: isPersonalDataAccepted,
        is_tos_accepted: isTosAccepted,
        no_yandex_account: noYandexAccount,
        is_passportizated: isPassportizated,
    } = student;

    const me = useSelector(getMe);

    const isAddingNewStudent = useSelector(getIsAddingNewStudent);
    const newStudentEditedName = useSelector(getNewStudentEditingStudentName);

    const canEdit =
        useSelector<ReduxUserStore, boolean>((storeState) => getCanEditClass(storeState, classId)) && !isArchiveClass;
    const currentEditingStudentIndex = useSelector(getCurrentEditingStudentIndex);
    const editedName = useSelector((storeState) => getCurrentEditingStudentName(storeState, { studentId: student.id }));
    const editStudentNameStatus = useSelector(getEditStudentNameStatus);

    const isParent = useSelector(getIsParent);

    const isEditingNow = id === currentEditingStudentIndex || id === NEW_STUDENT_ID;
    const isAccepted = !isParent && isPersonalDataAccepted && isTosAccepted;
    const isEditIconDisabled = currentEditingStudentIndex !== null || isAddingNewStudent;

    const studentValidation = useSelector((storeState) => getStudentValidationById(storeState, { studentId: id }));

    const showNameError = studentValidation && !studentValidation.correct && studentValidation.isMigrating;
    const isStudentValid = errorMsgId === null;

    const nameErrorMsgId = 'addClass.validation.incorrectSurnameOrName';

    const handleInputChange = ({ target: { value } }: React.ChangeEvent & { target: { value: string } }) => {
        onInputChange();

        dispatch(setCurrentEditingStudentName({ id, name: value.replace(/\s+/g, ' ') }));
    };

    const handleInputBlur = () => {
        if (isStudentValid && id !== NEW_STUDENT_ID) {
            dispatch(setStudentValidation(id, { isMigrating: false, correct: true }));
            dispatch(editStudentNameRequest(id, editedName));
        }

        onInputBlur();
    };

    const handleEnterKeyDown: React.KeyboardEventHandler = ({ keyCode }) => {
        if (keyCode === KEY_CODES.ENTER) {
            if (isStudentValid) {
                if (id === NEW_STUDENT_ID) {
                    ym('reachGoal', 'student_created', {
                        studentNumber: 1,
                        team_id: classId,
                        user_id: me?.id,
                        location: YM_LOCATION.CLASS,
                        before_students_count: studentsCount,
                    });
                    dispatch(saveNewStudentRequest(classId, newStudentEditedName!, loginId!));
                } else {
                    dispatch(setStudentValidation(id, { isMigrating: false, correct: true }));
                    dispatch(editStudentNameRequest(id, editedName));
                }
            }

            onEnterKeyDown();
        }
    };

    const handleInputFocus = () => {
        onInputFocus();
    };

    return (
        <TableCell mix="students__table-student-name">
            {isEditingNow ? (
                <div className="students__table-edited-name-wrapper">
                    <div className="students__table-edited-name-with-pencil">
                        <Input
                            disabled={editStudentNameStatus === LOADING_STATUSES.LOADING}
                            focusInputEnd
                            focused={focused}
                            maxLength={STUDENT_NAME_MAX_LENGTH}
                            mix="students__table-name-input"
                            onBlur={handleInputBlur}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onKeyDown={handleEnterKeyDown}
                            placeholder={<FormattedMessage id="class.table.field.students" />}
                            valid={shouldShowError ? 'no' : 'uncertain'}
                            value={editedName}
                        />
                    </div>
                    <div className={b('students', 'table-student-name-tip', { invalid: shouldShowError })}>
                        <FormattedMessage id={shouldShowError && errorMsgId ? errorMsgId : 'class.table.name.tip'} />
                    </div>
                </div>
            ) : (
                <div className="students__table-name-wrapper">
                    <div className="students__table-name-content">
                        <Text className="students__table-student-name-span" size={Text.SIZE.XL}>
                            {getStudentName(firstName, lastName)}
                        </Text>
                        {canEdit && !isArchiveClass && noYandexAccount && (
                            <StudentNamePencilIcon
                                className="students__table-student-name-edit"
                                disabled={isEditIconDisabled}
                                handleNameChange={onNameChange}
                            />
                        )}
                        {isAccepted && <AcceptedIcon className="students__table-student-name-accepted" />}
                        {isPassportizated && (
                            <span className="students__table-student-name-passportizated">
                                <ThemeProvider isRoot={false} theme={hrLight}>
                                    <TooltipStateful
                                        content="Привязан Яндекс ID"
                                        defaultVisible={false}
                                        direction="right"
                                        hasTail={true}
                                        scope="inplace"
                                        size="s"
                                        trigger="hover"
                                        view="default"
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path
                                                d="M12.7 15.6092V8.51704C13.6239 8.54191 14.4145 8.6341 15.031 8.95525C15.3935 9.14408 15.6918 9.41458 15.9133 9.84088C16.1436 10.2841 16.3229 10.9594 16.3229 11.9957C16.3229 13.0085 16.1306 13.6845 15.8808 14.1413C15.6362 14.5886 15.3016 14.8902 14.9003 15.1056C14.2753 15.4411 13.5033 15.5651 12.7 15.6092Z"
                                                fill="#262633"
                                            />
                                            <path
                                                clipRule="evenodd"
                                                d="M12.0101 23.6666C21.3473 23.6666 23.6784 21.3355 23.6784 11.9984C23.6784 2.66118 21.3473 0.330078 12.0101 0.330078C2.6729 0.330078 0.341797 2.66118 0.341797 11.9984C0.341797 21.3355 2.6729 23.6666 12.0101 23.6666ZM18.6563 11.9957C18.6563 10.7032 18.4342 9.6318 17.9839 8.7651C17.5248 7.88149 16.8633 7.27883 16.109 6.88589C14.732 6.16851 13.05 6.17233 11.8704 6.175L11.7647 6.17522C10.9926 6.17653 10.3667 6.80269 10.3667 7.57549V16.5656C10.3667 17.3347 10.9896 17.9674 11.7693 17.9654C12.8443 17.9627 14.5579 17.9377 16.0039 17.1614C16.7587 16.7562 17.4429 16.1478 17.928 15.2609C18.4079 14.3833 18.6563 13.3008 18.6563 11.9957ZM8.80127 7.3424C8.80127 6.69807 8.27894 6.17573 7.6346 6.17573C6.99027 6.17573 6.46794 6.69807 6.46794 7.3424V16.7992C6.46794 17.4435 6.99027 17.9659 7.6346 17.9659C8.27894 17.9659 8.80127 17.4435 8.80127 16.7992V7.3424Z"
                                                fill="#262633"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </TooltipStateful>
                                </ThemeProvider>
                            </span>
                        )}
                    </div>
                    {showNameError && (
                        <div className={b('students', 'table-student-name-tip', { invalid: true })}>
                            <FormattedMessage id={nameErrorMsgId} />
                        </div>
                    )}
                </div>
            )}
        </TableCell>
    );
};
